import React, {memo, useCallback, useRef, useState} from 'react';

import './index.scss';
import iconSave from '../../assets/images/icons/media/save.png';
import iconTv from '../../assets/images/icons/media/tv.png';
import iconAirConditioner from '../../assets/images/icons/media/air-conditioner.png';
import iconMusicPlayer from '../../assets/images/icons/media/music-player.png';
import iconSatellite from '../../assets/images/icons/media/satellite.png';
import iconProjection from '../../assets/images/icons/media/projection.png';
import iconOther from '../../assets/images/icons/media/other.png';
import {useKeyboard} from '../../hooks/keyboard';
import {useRPC} from '../../hooks/rpc';
import {setMediaScreenAction} from '../../store/ui/actions';
import {useDispatch, useSelector} from 'react-redux';
import {Container} from '../../components/Container';
import {useHistory, useParams} from 'react-router-dom';
import {useMessage} from '../../hooks/message';
import {onClickHandler} from '../../utils/onclick';
import {useSetSelector} from '../../hooks/set-selector';
import {useFilter} from '../../hooks/filter';
import {useLocalization} from "../../hooks/localization";


const MyButton = memo(function MyButton({type, selected, onClick}: { type: string; selected: boolean; onClick: (type: string) => void }) {
    let icon = iconOther;
    switch (type) {
        case 'air-conditioner':
            icon = iconAirConditioner;
            break;
        case 'tv':
            icon = iconTv;
            break;
        case 'music-player':
            icon = iconMusicPlayer;
            break;
        case 'satellite':
            icon = iconSatellite;
            break;
        case 'projection':
            icon = iconProjection;
            break;
    }

    let style = 'button';
    if (!selected) {
        style += ' no-border';
    }

    return (
        <div className={style} {...onClickHandler(() => onClick(type))}>
            <img src={icon} alt=""/>
        </div>
    );
});

const MyRadioButton = memo(function MyRadioButton({label, id, selected, onClick}: { selected: boolean; label: string; id: string; onClick: (id: string) => void; }) {
    const onPress = useCallback(() => {
        onClick(id);
    }, [id, onClick]);

    return (
        <div className="radio-input" {...onClickHandler(onPress)}>
            <div className="cursor">
                {renderIf(selected)(() => <div className="interior-cursor"/>)}
            </div>
            <span>{label}</span>
        </div>
    );
});

export const MediaCreateScreen = memo(function MediaCreateScreen() {
    const {t} = useLocalization();
    // @ts-ignore
    const {id} = useParams();
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    let allAreaList = useSelector(state => state.control.areaList)
    const areaList = allAreaList.filter(item => item.parent_area_id === (id ? id : ""))

    const dispatch = useDispatch();
    const history = useHistory();
    const {failure} = useMessage();

    const [rpc] = useRPC();
    const inputRef = useRef<HTMLInputElement>();
    const [setActiveInput, renderKeyboard] = useKeyboard();
    const [mediaName, setMediaName] = useState('');
    const [bridgeDeviceId, setBridgeDeviceId] = useState('');
    const [areaId, setAreaId] = useState("")
    const [selectedType, setSelectedType] = useState('air-conditioner');

    const devices = useFilter(
        bridgeDeviceList,
        device => bridgeDeviceTypeSet[device.type_id].is_media_device,
        [bridgeDeviceTypeSet],
    );

    const onFocus = useCallback(() => {
        setActiveInput([inputRef, mediaName, setMediaName]);
    }, [mediaName, setActiveInput]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    const save = useCallback(() => {
        if (mediaName == null || mediaName.trim().length < 3) {
            failure(t('REMOTE_NAME_MUST_CONTAIN_AT_LEAST_3_CHARACTERS'));
            return;
        }

        if (bridgeDeviceId == null || bridgeDeviceId === '') {
            failure(t('PLS_SELECT_A_DEVICE'));
            return;
        }

        rpc.createMediaDevice({name: mediaName, type: selectedType, bridge_device_id: bridgeDeviceId, area_id: areaId})
            .then(media => {
                history.push('/media-remote');
                dispatch(setMediaScreenAction(media, true));
                setSelectedType('air-conditioner');
                setMediaName('');
                setBridgeDeviceId('');
                setAreaId('');
            });
    }, [t, failure, dispatch, history, bridgeDeviceId, mediaName, rpc, selectedType, areaId]);

    return (
        <Container>
            <div className="media-create-screen media">
                <div className="content-header">
                    <div className="content-wrapper">
                        <div className="actions">
                            <input
                                maxLength={20}
                                placeholder={t('WRITE_REMOTE_NAME_2')}
                                ref={inputRef}
                                onChange={e => setMediaName(e.target.value)}
                                className="highlighted"
                                onFocus={onFocus} onBlur={onBlur}
                                value={mediaName}/>
                            <div className="action" {...onClickHandler(() => save())}>
                                <img src={iconSave} alt=''/>
                                <span>{t('SAVE')}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <span>{t('SELECT_REMOTE_TYPE')}</span>
                        <div className="remote-type-line">
                            <MyButton
                                type='air-conditioner'
                                onClick={setSelectedType}
                                selected={selectedType === 'air-conditioner'}/>
                            <MyButton
                                type='tv'
                                onClick={setSelectedType}
                                selected={selectedType === 'tv'}/>
                            <MyButton
                                type='music-player'
                                onClick={setSelectedType}
                                selected={selectedType === 'music-player'}/>
                            <MyButton
                                type='satellite'
                                onClick={setSelectedType}
                                selected={selectedType === 'satellite'}/>
                            <MyButton
                                type='projection'
                                onClick={setSelectedType}
                                selected={selectedType === 'projection'}/>
                            <MyButton
                                type='other'
                                onClick={setSelectedType}
                                selected={selectedType === 'other'}/>
                        </div>
                        <div className='floor-options-container'>
                            <span>{t('select_floor_or_room')}</span>
                            <div className='floor-options'>
                                {
                                areaList.map(area => <MyRadioButton
                                    key={`area_${area.id}`}
                                    id={area.id}
                                    label={area.name}
                                    onClick={setAreaId}
                                    selected={areaId === area.id}/>)
                                }
                            </div>
                        </div>
                    </div>
                    <div className="content-wrapper">
                        <span>{t('SELECT_DEVICE')}</span>
                        {
                            devices.map(device => <MyRadioButton
                                key={`device_${device.id}`}
                                id={device.id}
                                label={device.name}
                                onClick={setBridgeDeviceId}
                                selected={bridgeDeviceId === device.id}/>)
                        }
                    </div>
                </div>
            </div>
            {
                renderKeyboard()
            }
        </Container>
    );
});
